import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Eye, MessageSquareText, Plus } from "lucide-react";
import { messageTemplateSave } from "@shared/schemas/automation";
import { isReservedTemplateKey, unknownPlaceholders } from "@shared/validation/automation";
import { evaluation, parseInput } from "@shared/validation/issues";
import { errorMessage } from "@web/lib/api";
import { useFormValidation } from "@web/lib/formValidation";
import { messageTemplatesQuery, templateApi, templatePreviewQuery, type MessageTemplateRow } from "@web/lib/queries-automation";
import { useMe } from "@web/lib/session";
import {
  BottomSheet, Button, Card, Disclosure, EmptyState, Inline, Page, Row, RowGroup, SearchBar,
  Region, SegmentedControl, Skeleton, Stack, StatusChip, Text, TextArea, TextField, Toggle,
  UnsavedChanges, ValidationSummary, Workspace, useToast,
} from "@web/ui";
import { AdminsOnly } from "./Settings";

/** Settings › Message templates (SPEC §6.1 item 13). Every automated message the
 * venue sends is written here, and the preview is the point: nobody should have to
 * guess what `{{amount}}` will look like in a real email. */
const PLACEHOLDER_HELP = "{{first_name}} {{last_name}} {{company}} {{venue}} {{event_date}} {{amount}} {{due_date}} {{link}} {{agent_name}} {{agent_phone}} {{unsubscribe_url}}";

const groupOf = (key: string) => key.startsWith("drip.") ? "Sales sequences" : key.startsWith("reminder.") ? "Reminders" : "Other";

export function SettingsMessageTemplates() {
  const me = useMe();
  const templates = useQuery({ ...messageTemplatesQuery, enabled: me.companyAdmin });
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<MessageTemplateRow | null>(null);
  const [creating, setCreating] = useState(false);
  if (!me.companyAdmin) return <AdminsOnly />;

  const rows = (templates.data ?? []).filter(row =>
    !search || `${row.key} ${row.name ?? ""} ${row.notes ?? ""}`.toLowerCase().includes(search.toLowerCase()));
  const groups = ["Reminders", "Sales sequences", "Other"].map(name => ({ name, rows: rows.filter(row => groupOf(row.key) === name) })).filter(group => group.rows.length);

  return <Page>
    <Stack gap={4}>
      <Text variant="callout" tone="secondary">
        These are the words the automation sends. Edit them freely; the keys are what the reminders look up, so they cannot be renamed.
      </Text>
      <SearchBar value={search} onChange={setSearch} placeholder="Search templates" />
      {templates.isError
        ? <EmptyState icon={MessageSquareText} title="Templates could not be loaded" body={errorMessage(templates.error)}
            action={<Button onClick={() => void templates.refetch()}>Try again</Button>} />
        : !templates.data ? <Skeleton count={5} />
        : rows.length === 0 ? <EmptyState icon={MessageSquareText} title={search ? "Nothing matches" : "No templates yet"} body={search ? "Try a different word." : "Seeded templates appear here."} />
          : <Workspace variant="overview">{groups.map((group, index) => <Region key={group.name} area={index === 0 ? "main" : "aside"} label={group.name}><Stack gap={2}>
            <Text as="h2" variant="title">{group.name}</Text>
            <RowGroup>
              {group.rows.map(row => <Row key={row.id}
                title={row.name ?? row.key}
                subtitle={`${row.key}${row.notes ? ` · ${row.notes}` : ""}`}
                wrap
                trailing={<Stack gap={1} align="end">
                  <StatusChip tone={row.active ? "success" : "neutral"}>{row.active ? "On" : "Off"}</StatusChip>
                  <StatusChip tone="neutral">{[row.emailBody ? "Email" : null, row.smsBody ? "Text" : null].filter(Boolean).join(" · ") || "Empty"}</StatusChip>
                </Stack>}
                onClick={() => setEditing(row)} />)}
            </RowGroup>
          </Stack></Region>)}</Workspace>}
      <Button variant="secondary" icon={<Plus aria-hidden size={18} />} onClick={() => setCreating(true)}>Add a template</Button>
    </Stack>
    {editing && <TemplateSheet key={editing.id} template={editing} onClose={() => setEditing(null)} />}
    {creating && <TemplateSheet onClose={() => setCreating(false)} />}
  </Page>;
}

function TemplateSheet({ template, onClose }: { template?: MessageTemplateRow; onClose: () => void }) {
  const toast = useToast();
  const existing = Boolean(template);
  const [form, setForm] = useState({
    key: template?.key ?? "",
    name: template?.name ?? "",
    emailSubject: template?.emailSubject ?? "",
    emailBody: template?.emailBody ?? "",
    smsBody: template?.smsBody ?? "",
    notes: template?.notes ?? "",
    active: template ? template.active === 1 : true,
  });
  const [baseline] = useState(form);
  const [previewChannel, setPreviewChannel] = useState<"email" | "sms">("email");
  const [previewing, setPreviewing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const saved = useState({ done: false })[0];

  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);
  const unknown = unknownPlaceholders(form.smsBody, form.emailSubject, form.emailBody);
  const parsed = parseInput(messageTemplateSave, {
    key: form.key || "placeholder.key", name: form.name,
    ...(form.emailSubject ? { emailSubject: form.emailSubject } : {}),
    ...(form.emailBody ? { emailBody: form.emailBody } : {}),
    ...(form.smsBody ? { smsBody: form.smsBody } : {}),
    ...(form.notes ? { notes: form.notes } : {}),
    active: form.active,
  }, { type: "message_template", ...(template ? { id: template.id } : {}) }, "message_template.save");
  const localIssues = [
    ...(dirty && !parsed.success ? parsed.issues : []),
    ...unknown.map(name => ({
      code: "message_template.unknown_placeholder", kind: "error" as const,
      entity: { type: "message_template" as const, ...(template ? { id: template.id } : {}) },
      fields: ["emailBody"], blocks: ["message_template.save" as const],
      message: `Nothing can fill in {{${name}}}. A client would receive the braces.`,
    })),
  ];
  const validation = useFormValidation(evaluation(localIssues, false));

  const preview = useQuery({ ...templatePreviewQuery(template?.id ?? "", previewChannel), enabled: previewing && Boolean(template) });

  function change<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    validation.edit(() => { saved.done = false; setForm(previous => ({ ...previous, [key]: value })); });
  }

  async function submit() {
    if (!dirty || localIssues.length || !validation.begin("message_template.save")) return;
    try {
      if (template) {
        await templateApi.update(template.id, {
          name: form.name, emailSubject: form.emailSubject || null, emailBody: form.emailBody || null,
          smsBody: form.smsBody || null, notes: form.notes || null, active: form.active,
        });
      } else {
        await templateApi.create({
          key: form.key, name: form.name,
          emailSubject: form.emailSubject || null, emailBody: form.emailBody || null,
          smsBody: form.smsBody || null, notes: form.notes || null, active: form.active,
        });
      }
      saved.done = true;
      validation.clear();
      toast.success(template ? "Template saved" : "Template added");
      onClose();
    } catch (error) { validation.reject(error); toast.error(errorMessage(error)); }
    finally { validation.finish(); }
  }

  // The same rule the service enforces, not a second copy of it.
  const reserved = isReservedTemplateKey(form.key);

  return <>
    <UnsavedChanges dirty={dirty} pending={validation.busy} canLeave={() => saved.done} />
    <BottomSheet open onClose={() => (dirty ? setConfirmingClose(true) : onClose())} title={existing ? (template?.name ?? template?.key ?? "Template") : "New template"}
      footer={<Button loading={validation.busy} disabled={!dirty || localIssues.length > 0} onClick={() => void submit()}>{existing ? "Save template" : "Add template"}</Button>}>
      <Stack gap={3}>
        {!existing && <TextField label="Key" value={form.key} placeholder="custom.welcome" hint="Lower case letters, numbers and dots. This is how the template is looked up." onChange={event => change("key", event.target.value)} {...validation.field("key")} />}
        {existing && <Card><Stack gap={1}><Text variant="caption" tone="secondary">Key</Text><Text>{template?.key}</Text></Stack></Card>}
        <TextField label="Name" value={form.name} onChange={event => change("name", event.target.value)} {...validation.field("name")} />
        <Toggle label="Send this template" checked={form.active} onChange={value => change("active", value)} />
        {reserved && !form.active && <Text variant="caption" tone="danger">While this is off, the reminder that uses it sends nothing.</Text>}

        <TextField label="Email subject" value={form.emailSubject} onChange={event => change("emailSubject", event.target.value)} {...validation.field("emailSubject")} />
        <TextArea label="Email body" rows={10} value={form.emailBody} onChange={event => change("emailBody", event.target.value)} {...validation.field("emailBody")} />
        <TextArea label="Text message" rows={4} value={form.smsBody} hint="Leave blank if this template is email only." onChange={event => change("smsBody", event.target.value)} {...validation.field("smsBody")} />
        <TextArea label="What this is for" rows={2} value={form.notes} onChange={event => change("notes", event.target.value)} {...validation.field("notes")} />

        <Disclosure title="Placeholders you can use">
          <Text variant="caption" tone="secondary">{PLACEHOLDER_HELP}</Text>
        </Disclosure>

        {existing && <Stack gap={2}>
          <Button variant="secondary" icon={<Eye aria-hidden size={18} />} onClick={() => setPreviewing(true)}>Preview with sample details</Button>
          {previewing && <Card><Stack gap={2}>
            <SegmentedControl items={[{ key: "email", label: "Email" }, { key: "sms", label: "Text" }]} value={previewChannel} onChange={setPreviewChannel} />
            {preview.isFetching && <Skeleton count={2} />}
            {preview.data?.issues.length ? <Text tone="danger">Nothing can fill in {preview.data.issues.map(name => `{{${name}}}`).join(", ")}. This template would not send.</Text> : null}
            {preview.data && !preview.data.issues.length && <Stack gap={2}>
              {preview.data.subject && <Text variant="title">{preview.data.subject}</Text>}
              {/* The words as the client will read them: a paragraph per block,
                  because a preview run together into one line proves nothing. */}
              {preview.data.body.split(/\n{2,}/).map((block, index) => <Text key={index}>{block}</Text>)}
            </Stack>}
            {preview.isError && <Text tone="danger">The preview could not be loaded.</Text>}
          </Stack></Card>}
        </Stack>}

        <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />
        {existing && !reserved && <Button variant="secondary" onClick={() => setRemoving(true)}>Delete this template</Button>}
        {existing && reserved && <Text variant="caption" tone="secondary">This template is used by the automation and cannot be deleted. Turn it off instead.</Text>}
      </Stack>
    </BottomSheet>
    {/* A sheet's close gesture is a phone's back button; the router guard cannot
        see it, so a half-written record asks before it is discarded. */}
    <BottomSheet open={confirmingClose} onClose={() => setConfirmingClose(false)} title="Keep your changes?"
      footer={<Button onClick={() => setConfirmingClose(false)}>Keep editing</Button>}>
      <Stack gap={3}>
        <Text>You have made changes but not saved them.</Text>
        <Button variant="secondary" onClick={() => { setConfirmingClose(false); onClose(); }}>Discard my changes</Button>
      </Stack>
    </BottomSheet>
    {removing && template && <BottomSheet open onClose={() => setRemoving(false)} title="Delete this template?"
      footer={<Button loading={validation.busy} onClick={async () => {
        try { await templateApi.remove(template.id); toast.success("Template deleted"); onClose(); }
        catch (error) { toast.error(errorMessage(error)); }
      }}>Delete it</Button>}>
      <Stack gap={2}><Text>{template.name ?? template.key} will be removed.</Text><Text tone="secondary">Messages already sent are unaffected.</Text></Stack>
    </BottomSheet>}
  </>;
}
